import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import {
  CommonAnalyticsParams,
  getCommonAnalyticsParams,
  updateCommonAnalyticsParams,
  DEFAULT_COMMON_ANALYTICS_PARAMS,
  HexParam,
} from "@services/adminService";

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const SectionSubtitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 100px;
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ error }) => (error ? "#e74c3c" : "#68ad18")};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HexBlock = styled.div`
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 6px;
`;

const HexRowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-top: 8px;
`;

const CommonAnalyticsParamsTab: React.FC = () => {
  const [params, setParams] = useState<CommonAnalyticsParams | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  // A failed load shows the built-in defaults; saving them would overwrite the
  // stored params — so no save until a load succeeds.
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchParams = useCallback(async () => {
    setLoading(true);
    try {
      const { params: p } = await getCommonAnalyticsParams();
      setParams(p);
      setLoadFailed(false);
    } catch {
      setParams(DEFAULT_COMMON_ANALYTICS_PARAMS);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParams();
  }, [fetchParams]);

  const handleSave = async () => {
    if (!params) return;
    setMessage(null);
    setSaving(true);
    try {
      await updateCommonAnalyticsParams(params);
      setMessage({ text: "Saved successfully", error: false });
      fetchParams();
    } catch (e: unknown) {
      setMessage({ text: (e as Error).message || "Failed to save", error: true });
    } finally {
      setSaving(false);
    }
  };

  const setCommon = (updates: Partial<CommonAnalyticsParams>) => {
    setParams((prev) => (prev ? { ...prev, ...updates } : null));
  };

  const setHex = (key: "12" | "13" | "14", updates: Partial<HexParam>) => {
    setParams((prev) =>
      prev ? { ...prev, hex: { ...prev.hex, [key]: { ...prev.hex[key], ...updates } } } : null
    );
  };

  const setPotatoHexNPoints = (hexKey: string, value: number) => {
    setParams((prev) =>
      prev
        ? { ...prev, potato: { hex_n_points: { ...prev.potato.hex_n_points, [hexKey]: value } } }
        : null
    );
  };

  const setGrainHexNPoints = (hexKey: string, value: number) => {
    setParams((prev) =>
      prev
        ? { ...prev, grain: { ...prev.grain, hex_n_points: { ...prev.grain.hex_n_points, [hexKey]: value } } }
        : null
    );
  };

  const setGrainHexNAggPoints = (hexKey: string, value: number) => {
    setParams((prev) =>
      prev
        ? { ...prev, grain: { ...prev.grain, hex_n_agg_points: { ...prev.grain.hex_n_agg_points, [hexKey]: value } } }
        : null
    );
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  if (!params) return null;

  return (
    <div>
      <Section>
        <SectionTitle>Common parameters</SectionTitle>
        <Text size={13} color="#899093" style={{ marginBottom: 16 }}>
          Global processing thresholds and coefficients. The backend calibration reads
          potato / grain hex_n_points (resolution 13); the other values are not used by
          any algorithm yet.
        </Text>
      </Section>

      <Card>
        <SectionTitle>Common parameters</SectionTitle>
        <Text size={11} color="#6b7280" style={{ marginBottom: 12 }}>
          speedLimit: if not set on device, speed above which we consider there was no harvesting.
          yieldCoeff: coefficient for converting kg to centners per hectare. overwriteThresh: if kg
          difference below threshold, do not update field; else update fields with few new points.
          areaThresh (ha): below = hex13, above = hex12. minCount: if fewer points in field, do not
          calculate.
        </Text>
        <FormGrid>
          <FieldWrap>
            <Text size={12} color="#899093">speedLimit</Text>
            <Input
              type="number"
              step="0.1"
              value={params.speedLimit}
              onChange={(e) => setCommon({ speedLimit: parseFloat(e.target.value) || 0 })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">yieldCoeff</Text>
            <Input
              type="number"
              step="0.01"
              value={params.yieldCoeff}
              onChange={(e) => setCommon({ yieldCoeff: parseFloat(e.target.value) || 0 })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">overwriteThresh</Text>
            <Input
              type="number"
              step="0.01"
              value={params.overwriteThresh}
              onChange={(e) => setCommon({ overwriteThresh: parseFloat(e.target.value) || 0 })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">areaThresh (ha)</Text>
            <Input
              type="number"
              step="1"
              value={params.areaThresh}
              onChange={(e) => setCommon({ areaThresh: parseFloat(e.target.value) || 0 })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">minCount</Text>
            <Input
              type="number"
              value={params.minCount}
              onChange={(e) => setCommon({ minCount: parseInt(e.target.value, 10) || 0 })}
            />
          </FieldWrap>
        </FormGrid>
      </Card>

      <Card>
        <SectionTitle>Hex parameters</SectionTitle>
        <SectionSubtitle>Hexagon 12, 13, 14 configuration</SectionSubtitle>
        {(["12", "13", "14"] as const).map((key) => (
          <HexBlock key={key}>
            <Text size={12} type="heading" color="#494c5b">Hexagon {key}</Text>
            <HexRowGrid>
              <FieldWrap>
                <Text size={11} color="#899093">resolution</Text>
                <Input
                  type="number"
                  value={params.hex[key].resolution}
                  onChange={(e) =>
                    setHex(key, { resolution: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </FieldWrap>
              <FieldWrap>
                <Text size={11} color="#899093">surface (ha)</Text>
                <Input
                  type="number"
                  step="0.0001"
                  value={params.hex[key].surface}
                  onChange={(e) =>
                    setHex(key, { surface: parseFloat(e.target.value) || 0 })
                  }
                />
              </FieldWrap>
              <FieldWrap>
                <Text size={11} color="#899093">kring</Text>
                <Input
                  type="number"
                  value={params.hex[key].kring}
                  onChange={(e) =>
                    setHex(key, { kring: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </FieldWrap>
              <FieldWrap>
                <Text size={11} color="#899093">n_points</Text>
                <Input
                  type="number"
                  value={params.hex[key].n_points}
                  onChange={(e) =>
                    setHex(key, { n_points: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </FieldWrap>
              <FieldWrap>
                <Text size={11} color="#899093">n_agg_points</Text>
                <Input
                  type="number"
                  value={params.hex[key].n_agg_points}
                  onChange={(e) =>
                    setHex(key, { n_agg_points: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </FieldWrap>
            </HexRowGrid>
          </HexBlock>
        ))}
      </Card>

      <Card>
        <SectionTitle>Potato processing parameters</SectionTitle>
        <FormGrid>
          {(["12", "13", "14"] as const).map((key) => (
            <FieldWrap key={key}>
              <Text size={12} color="#899093">hex_n_points [{key}]</Text>
              <Input
                type="number"
                value={params.potato.hex_n_points[key] ?? 0}
                onChange={(e) =>
                  setPotatoHexNPoints(key, parseInt(e.target.value, 10) || 0)
                }
              />
            </FieldWrap>
          ))}
        </FormGrid>
      </Card>

      <Card>
        <SectionTitle>Grain processing parameters</SectionTitle>
        <FormGrid>
          {(["12", "13", "14"] as const).map((key) => (
            <FieldWrap key={key}>
              <Text size={12} color="#899093">hex_n_points [{key}]</Text>
              <Input
                type="number"
                value={params.grain.hex_n_points[key] ?? 0}
                onChange={(e) =>
                  setGrainHexNPoints(key, parseInt(e.target.value, 10) || 0)
                }
              />
            </FieldWrap>
          ))}
          {(["12", "13", "14"] as const).map((key) => (
            <FieldWrap key={`agg-${key}`}>
              <Text size={12} color="#899093">hex_n_agg_points [{key}]</Text>
              <Input
                type="number"
                value={params.grain.hex_n_agg_points[key] ?? 0}
                onChange={(e) =>
                  setGrainHexNAggPoints(key, parseInt(e.target.value, 10) || 0)
                }
              />
            </FieldWrap>
          ))}
        </FormGrid>
      </Card>

      <Actions>
        <ActionButton onClick={handleSave} disabled={saving || loadFailed}>
          {saving ? "Saving..." : "Save"}
        </ActionButton>
      </Actions>
      {message && <Message error={message.error}>{message.text}</Message>}
    </div>
  );
};

export default CommonAnalyticsParamsTab;
